import "systemjs/dist/system.min.js";
import { createApp, watch } from "vue";
import * as singleSpa from "single-spa";
import App from "@/shell/App.vue";
import { pinia } from "@/plugins/pinia";
import { createShellVuetify } from "@/plugins/vuetify";
import { applyTenantDefaultTheme, installGlobalState } from "@/state/globalState";
import { applyBranding } from "@/tenant/branding";
import { fetchTenantApp, resolveTenantHost } from "@/tenant/resolve";
import { paintBootSplash, readSplash, rememberSplash } from "@/tenant/splash";
import { applyDevImportMapOverrides, injectImportMap, registerTenantModules } from "@/spa/registry";
import { decideHomeRedirect, decideRouteGuard } from "@/nav/routeGuard";
import { routeUnderPrefix } from "@/nav/navFilter";
import { isDevHost, isTenantUsable } from "@/tenant/availability";
import { clearAutoRetries } from "@/tenant/retry";
import { applyPwaManifest } from "@/pwa/manifest";
import { watchInstallPrompt } from "@/pwa/install";
import { registerServiceWorker } from "@/pwa/serviceWorker";
import { useSessionStore } from "@/stores/session";
import "@/styles/standalone.css";

function hideBootSplash(): void {
  document.getElementById("boot-splash")?.remove();
}

async function boot(): Promise<void> {
  // First, before any await: brand the pre-resolve splash with what THIS host
  // resolved last time (neutral on a first visit — see tenant/splash.ts).
  paintBootSplash(readSplash(window.location.hostname));
  const globalState = installGlobalState();
  // Registered BEFORE anything awaits: `beforeinstallprompt` fires once, on
  // Chromium's own schedule, and cannot be recovered if it is missed.
  watchInstallPrompt();
  const host = resolveTenantHost();

  const app = createApp(App);
  app.use(pinia);

  let tenant;
  try {
    tenant = await fetchTenantApp(host);
    // Only a REAL answer may rewrite the remembered splash brand — a
    // `found:false` forgets it, but a thrown fetch (offline, 5xx) is not a
    // resolution and must not wipe the brand for the next boot.
    rememberSplash(window.location.hostname, host, tenant);
  } catch {
    // Network/API failure resolving the tenant reads the same as "not found" —
    // Unavailable.vue is the correct terminal state either way.
    tenant = { found: false } as Awaited<ReturnType<typeof fetchTenantApp>>;
  }

  applyBranding(tenant.name || "MetaShip", tenant.branding);
  const vuetify = createShellVuetify(tenant.branding);
  // One theme for chrome AND module: the global state (what the hosted module
  // reads) decides, seeded from the tenant on a first visit.
  vuetify.theme.global.name.value = applyTenantDefaultTheme(globalState, tenant.branding?.defaultTheme);
  app.use(vuetify);

  const devHost = isDevHost(window.location.hostname, import.meta.env.DEV);
  const session = useSessionStore(pinia);
  session.setTenant(host, tenant, devHost);

  app.mount("#app");
  hideBootSplash();

  if (!isTenantUsable(tenant, devHost)) return;
  // The app resolved — a provisioning wait that ended here forgets its
  // automatic-retry count (see tenant/retry.ts).
  clearAutoRetries(window.location.hostname);

  // Installability, per tenant app (crm.metaship.ai and a customer's own
  // domain are separate installed apps with separate icons and names). Not
  // awaited — the manifest only decides whether the browser OFFERS to install
  // the app, so drawing icons must never sit in front of the sign-in path.
  void applyPwaManifest({ kind: tenant.kind, name: tenant.name || "Metaship", branding: tenant.branding });
  void registerServiceWorker();

  if (tenant.importMap.imports.length) {
    // `?mfe=<key>=<url>` — a local module build in place of the CDN one. Dev
    // hosts only, for the same reason as the tenant-host override above it.
    injectImportMap(devHost ? applyDevImportMapOverrides(tenant.importMap.imports) : tenant.importMap.imports);
  }

  // Reload fast-path (persisted, unexpired MetaShip session) → resume silently.
  // Otherwise fall back to a stored Auth0 refresh token; a genuinely fresh
  // visitor lands on Login.vue and waits for an explicit "Sign in" click.
  if (window.location.pathname !== "/auth0/callback" && !session.resumeFromPersistedSession()) {
    await session.resumeFromRefreshToken().catch(() => {
      /* Login.vue renders for status === "signed-out" */
    });
  }

  let modulesStarted = false;
  const startModulesOnceReady = () => {
    if (modulesStarted || session.status !== "ready" || !tenant) return;
    modulesStarted = true;

    registerTenantModules(tenant.modules);
    singleSpa.start();

    window.addEventListener("single-spa:before-routing-event", () => {
      const redirectTo = decideRouteGuard(window.location.pathname, window.location.search, tenant.modules);
      if (redirectTo) singleSpa.navigateToUrl(redirectTo);
    });

    const path = window.location.pathname;
    // Overlays (comms, help-center) match every path by DESIGN — that must
    // NEVER count as "already inside a module" for landing-page purposes, or
    // the presence of an overlay silently disables the home redirect entirely.
    const insideAModule = tenant.modules.some((m) => !m.overlay && routeUnderPrefix(path, m.pathPrefix));
    if (!insideAModule) {
      const home = decideHomeRedirect(tenant.homePath, tenant.modules, !!tenant.homePage?.enabled);
      if (home) singleSpa.navigateToUrl(home);
    }
  };

  watch(() => session.status, startModulesOnceReady, { immediate: true });
}

boot();
